type ErrorSeverity = 'low' | 'medium' | 'high' | 'critical';

interface ErrorReport {
  id: string;
  message: string;
  stack?: string;
  component?: string;
  severity: ErrorSeverity;
  timestamp: number;
  context?: Record<string, unknown>;
  handled: boolean;
  count: number;
  firstSeen: number;
  lastSeen: number;
}

type ErrorListener = (report: ErrorReport) => void;

const MAX_STORED_ERRORS = 50;
const DEDUP_WINDOW = 5000;

class ErrorMonitor {
  private errors: ErrorReport[] = [];
  private listeners: Set<ErrorListener> = new Set();
  private recentErrors: Map<string, { count: number; firstSeen: number; lastSeen: number }> = new Map();

  subscribe(listener: ErrorListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notify(report: ErrorReport) {
    this.listeners.forEach((fn) => {
      try { fn(report); } catch { /* ignore */ }
    });
  }

  report(error: Error | string, options?: {
    component?: string;
    severity?: ErrorSeverity;
    context?: Record<string, unknown>;
    handled?: boolean;
  }) {
    const message = typeof error === 'string' ? error : error.message;
    const stack = typeof error === 'string' ? undefined : error.stack;
    const severity = options?.severity || 'medium';
    const now = Date.now();

    const dedupKey = `${options?.component || 'unknown'}:${message}`;
    const existing = this.recentErrors.get(dedupKey);

    if (existing && (now - existing.lastSeen) < DEDUP_WINDOW) {
      existing.count++;
      existing.lastSeen = now;
      return;
    }

    const report: ErrorReport = {
      id: `${now}-${Math.random().toString(36).slice(2, 9)}`,
      message,
      stack: stack?.slice(0, 2000),
      component: options?.component,
      severity,
      timestamp: now,
      context: options?.context,
      handled: options?.handled ?? true,
      count: (existing?.count || 0) + 1,
      firstSeen: existing?.firstSeen || now,
      lastSeen: now,
    };

    this.recentErrors.set(dedupKey, { count: report.count, firstSeen: report.firstSeen, lastSeen: report.lastSeen });
    this.errors.unshift(report);
    if (this.errors.length > MAX_STORED_ERRORS) {
      this.errors.length = MAX_STORED_ERRORS;
    }

    if (severity === 'critical' || severity === 'high') {
      console.error('%c[ErrorMonitor]', 'color:red;font-weight:bold', 'High/Critical:', report);
    } else if (severity === 'medium') {
      console.warn('%c[ErrorMonitor]', 'color:orange', 'Medium:', report);
    } else {
      console.info('%c[ErrorMonitor]', 'color:gray', 'Low:', report);
    }

    this.notify(report);
  }

  getErrors(): ErrorReport[] {
    return [...this.errors];
  }

  getRecentUnhandled(): ErrorReport[] {
    return this.errors.filter((e) => !e.handled);
  }

  clear() {
    this.errors = [];
    this.recentErrors.clear();
  }
}

export const errorMonitor = new ErrorMonitor();

const IGNORE_PATTERNS = [
  /WebSocket/i,
  /vite/i,
  /ERR_ABORTED/i,
  /resizeObserver/i,
  /Script error/i,
  /Non-Error promise rejection/i,
];

function shouldIgnoreError(message: string, filename?: string): boolean {
  if (!message) return true;
  for (const p of IGNORE_PATTERNS) {
    if (p.test(message)) return true;
  }
  if (filename) {
    for (const p of IGNORE_PATTERNS) {
      if (p.test(filename)) return true;
    }
  }
  return false;
}

if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    if (event.error) {
      const msg = event.error.message || '';
      if (shouldIgnoreError(msg, event.filename)) return;
      errorMonitor.report(event.error, {
        component: 'global',
        severity: 'high',
        handled: false,
        context: { filename: event.filename, lineno: event.lineno, colno: event.colno },
      });
    }
  });

  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason instanceof Error
      ? event.reason
      : new Error(String(event.reason));
    const msg = error.message || '';
    if (shouldIgnoreError(msg)) return;
    errorMonitor.report(error, {
      component: 'global',
      severity: 'high',
      handled: false,
      context: { type: 'unhandledrejection' },
    });
  });
}

export function wrapFetch(url: string, init?: RequestInit): Promise<Response> {
  return fetch(url, init).catch((error) => {
    const msg = (error as any)?.message || '';
    if (shouldIgnoreError(msg)) throw error;
    errorMonitor.report(error, {
      component: 'fetch',
      severity: 'medium',
      context: { url, method: init?.method || 'GET' },
    });
    throw error;
  });
}

export type { ErrorReport, ErrorSeverity };
